/**
 * Org-member rows (from the org detail payload's `users`) → context-stack
 * switcher options for the workspace sidebar's user dropdown.
 *
 * Structural param type on purpose: both callers already hold the org detail
 * response, and depending on the generated type here would couple this util to
 * codegen output it only needs four fields of.
 */

import type { ContextSwitcherOption } from '../components/org-page-redesign/WorkspaceSidebar';

interface OrgMemberLike {
  user_id: string;
  name?: string | null;
  email?: string | null;
  status?: string | null;
}

/**
 * Beyond this many members the switcher menu should be capped and offer a
 * "view all" footer instead of scrolling forever. Production org sizes top
 * out around ~56 active users (checked 2026-08), so only a couple of orgs
 * ever hit the cap.
 */
export const USER_SWITCHER_MAX_OPTIONS = 30;

export function buildUserSwitcherOptions(
  users: OrgMemberLike[] | undefined,
): ContextSwitcherOption[] {
  if (!users?.length) return [];
  return [...users]
    .sort((a, b) =>
      (a.name || a.email || '').localeCompare(b.name || b.email || '', undefined, {
        sensitivity: 'base',
      }),
    )
    .map(user => {
      const email = user.email || undefined;
      return {
        id: user.user_id,
        label: user.name || email || user.user_id,
        secondary:
          user.status === 'inactive' ? [email, '(inactive)'].filter(Boolean).join(' ') : email,
      };
    });
}
